/**
 * @file promotion-config.js
 * @description Centralized configuration for all promotion types
 * @module GniibPromotions.Config
 *
 * This file contains all constants, limits, API endpoints, and localized messages.
 * When adding a new promotion type, add its configuration here first.
 */
(function(global) {
  'use strict';

  // Create namespace
  global.GniibPromotions = global.GniibPromotions || {};

  /**
   * Promotion type constants and limits
   * @namespace GniibPromotions.Config
   */
  global.GniibPromotions.Config = {

    // ============================================
    // SKHWC Staff Promotion
    // ============================================
    SKHWC: {
      MAX_QUANTITY: 16,           // Maximum 16 pieces per order
      MIN_DISCOUNT_QUANTITY: 2,   // Minimum 2 pieces for discount
      DISCOUNT_RATE: 0.2          // 20% discount (pay 80%)
    },

    // ============================================
    // FUBON Promotion
    // ============================================
    FUBON: {
      MIN_QUANTITY: 2,            // Minimum 2 pieces for discount
      MAX_DISCOUNTED: 10          // Maximum 10 pieces get discount
    },

    // ============================================
    // CITIBANK Promotion
    // ============================================
    CITIBANK: {
      MIN_QUANTITY: 2,            // Minimum 2 pieces for discount
      MAX_DISCOUNTED: 10          // Maximum 10 pieces get discount
    },

    // ============================================
    // CUMC Promotion (Monthly limits)
    // ============================================
    CUMC: {
      RULE1_MIN: 10,              // Minimum 10 pieces for Rule 1 (monthly total)
      RULE1_MAX: 20,              // Maximum 20 pieces for Rule 1 (monthly total)
      RULE2_MAX_PER_VARIANT: 20,  // Maximum 20 pieces per variant per month
      // ⚠️ BACKDOOR (testing only): customer IDs that bypass the period/time gate.
      // Other limits still apply. Remove before the promotion goes live to the public.
      PERIOD_BYPASS_CUSTOMER_IDS: ['7656120582213', '7656128249925']  // christsang@ysd.hk, rickylaw@g-niib.com
    },

    // ============================================
    // LIUYANG Promotion
    // ============================================
    LIUYANG: {
      RULE1_MIN_QUANTITY: 2,      // Minimum 2 pieces for discount
      GIFT_MULTIPLE: 8,           // Every 8 pieces gets free gifts
      GIFT_QUANTITY: 4,           // 4 free gifts per 8 items
      MAX_PER_VARIANT: 8          // Maximum 8 pieces per variant
    },

    // ============================================
    // Staff Promotion (Monthly limits)
    // ============================================
    STAFF: {
      PER_PRODUCT_LIMIT: 20       // Maximum 20 pieces per product per month
    },

    // ============================================
    // Partner Staff Promotion (Monthly hard limit)
    // ============================================
    PARTNER_STAFF: {
      PER_VARIANT_LIMIT: 10       // Maximum 10 pieces per variant per month (hard limit)
    },

    // ============================================
    // KWH Macau Promotion
    // ============================================
    KWH_MACAU: {
      DISCOUNT_RATE: 0.5          // 50% discount
    },

    // ============================================
    // Space VIP Promotion
    // ============================================
    SPACE_VIP: {
      DISCOUNT_RATE: 0.5          // 50% discount
    },

    // ============================================
    // IBD Group Promotion
    // ============================================
    IBD_GROUP: {
      DEFAULT_PER_VARIANT_LIMIT: 3,  // Default 3 pieces per variant
      DISCOUNT_RATE: 0.25            // 25% discount
    },

    // ============================================
    // DCH Auriga Promotion
    // ============================================
    DCH_AURIGA: {
      MIN_QUANTITY: 2,              // Minimum 2 pieces for discount
      MAX_QUANTITY: 10              // Maximum 10 pieces per order
    },

    // ============================================
    // Role to Handler Mapping
    // ============================================
    ROLE_TO_HANDLER: {
      'staff': 'Staff',
      'gniib staff': 'Staff',
      'microsigx staff': 'Staff',
      'skhwcstaff': 'Skhwc',
      'kwhmacau': 'KwhMacau',
      'ibd_group': 'IbdGroup',
      'fubon': 'Fubon',
      'cumc': 'Cumc',
      'liuyang': 'Liuyang',
      'citibank': 'Citibank',
      'space vip': 'SpaceVip',
      'partner staff': 'PartnerStaff',
      'dch_auriga': 'DchAuriga'
    },

    // ============================================
    // Role to Promotion Type Mapping
    // ============================================
    ROLE_TO_TYPE: {
      'staff': 'staff',
      'gniib staff': 'staff',
      'microsigx staff': 'staff',
      'skhwcstaff': 'skhwc_staff',
      'kwhmacau': 'kwh_macau',
      'ibd_group': 'ibd_group',
      'fubon': 'fubon',
      'cumc': 'cumc',
      'liuyang': 'liuyang',
      'citibank': 'citibank',
      'space vip': 'space_vip',
      'partner staff': 'partner_staff',
      'dch_auriga': 'dch_auriga'
    },

    // ============================================
    // SF Express Configuration
    // ============================================
    SF_EXPRESS: {
      ENABLED: true,
      EXCLUDED_ROLES: ['cumc', 'dch_auriga'],   // Roles that skip SF Express address selection
      TIMEOUT: 90000              // 90 second timeout for popup
    },

    // ============================================
    // Analytics Configuration
    // ============================================
    ANALYTICS: {
      ENABLED: false,
      GA4_MEASUREMENT_ID: 'G-T2WKZZ91F7'
    }
  };

  /**
   * API Endpoints by promotion type
   * @namespace GniibPromotions.ApiEndpoints
   */
  global.GniibPromotions.ApiEndpoints = {
    /**
     * Get API host based on environment
     * @returns {string} API host URL
     */
    getHost: function() {
      var isLocalhost = window.location.host.includes('127.0.0.1');
      return isLocalhost
        ? 'https://mastiff-haphazard-shove.ngrok-free.dev'
        : 'https://gniib.shopifyapp.ysd.hk';
    },

    /**
     * Get API endpoint for a promotion type
     * @param {string} promotionType - The promotion type
     * @returns {string} Full API endpoint URL
     */
    getEndpoint: function(promotionType) {
      var host = this.getHost();
      var endpoints = {
        'staff': '/api/promotions/staff',
        'skhwc_staff': '/api/promotions/skhwc-staff',
        'kwh_macau': '/api/promotions/kwh-macau',
        'ibd_group': '/api/promotions/ibd-group',
        'fubon': '/api/promotions/fubon',
        'cumc': '/api/promotions/cumc',
        'liuyang': '/api/promotions/liuyang',
        'citibank': '/api/promotions/citibank',
        'space_vip': '/api/promotions/space-vip',
        'partner_staff': '/api/promotions/partner-staff',
        'dch_auriga': '/api/promotions/dch-auriga'
      };
      return host + (endpoints[promotionType] || '');
    }
  };

  /**
   * Localized messages for SF Express popup
   * @namespace GniibPromotions.Messages
   */
  global.GniibPromotions.Messages = {
    SF_EXPRESS: {
      'en': {
        text: '📍 Due to security upgrades on the SF Express Hong Kong (SFHK) App, starting August 6, 2024, the SF Express Hong Kong (SFHK) App\'s package pickup notification function will replace SMS notifications. Customers can enable push notifications on their mobile phones to avoid missing any pickup reminders. Customers can also track their shipments in real time within the SF Express Hong Kong (SFHK) App. For any inquiries, please contact SF Express at (852) 2730 0273. ',
        linkText: 'Click to learn more',
        linkUrl: 'https://htm.sf-express.com/hk/en/news/detail/Reduction-of-SMS-Notifications-Full-Implementation-of-SFHK-App-Push-Notifications/'
      },
      'zh-TW': {
        text: '📍因順豐香港 SFHK APP 保安升級，由2024年8月6日起，順豐香港 SFHK APP 取件通知功能將取代以 SMS 短訊形式通知客戶取件。客戶可以開啟手機推送通知，以免錯過貨件運送提醒通知，同時客戶亦可於順豐香港SFHK APP內實時追蹤貨件狀況。如有任何疑問，歡迎致電 (852) 2730 0273聯絡順豐速運。',
        linkText: '了解更多',
        linkUrl: 'https://htm.sf-express.com/hk/tc/news/detail/Notice-of-SFHK-APP-Security-Upgrade/'
      },
      'zh-CN': {
        text: '📍因顺丰香港 SFHK APP 保安升级，由2024年8月6日起，顺丰香港 SFHK APP 取件通知功能将取代以 SMS 短讯形式通知客户取件。客户可以开启手机推送通知，以免错过货件运送提醒通知，同时客户亦可于顺丰香港SFHK APP内实时追踪货件状况。如有任何疑问，欢迎致电 (852) 2730 0273联络顺丰速运。',
        linkText: '了解更多',
        linkUrl: 'https://htm.sf-express.com/hk/sc/news/detail/Reduction-of-SMS-Notifications-Full-Implementation-of-SFHK-App-Push-Notifications/'
      }
    },

    /**
     * Get SF Express message for current language
     * @returns {Object} Message object with text, linkText, linkUrl
     */
    getSFExpressMessage: function() {
      var lang = document.documentElement.lang || 'zh-TW';
      if (lang.startsWith('en')) lang = 'en';
      else if (lang === 'zh-CN' || lang === 'zh-Hans') lang = 'zh-CN';
      else lang = 'zh-TW';
      return this.SF_EXPRESS[lang];
    },

    // ============================================
    // Error Messages
    // ============================================
    ERRORS: {
      LOGIN_REQUIRED: '請先登入/註冊帳戶，然後返回此頁面。',
      ROLE_NOT_FOUND: 'Customer role not found. Please contact support if you believe you should have access to this promotion.',
      NOT_ELIGIBLE: 'This promotion is only available for qualified members. Your current role does not qualify for this promotion.',
      CHECKOUT_ERROR: '結帳過程中發生錯誤，請重試。',
      LOAD_ERROR: '無法載入促銷資訊，請稍後再試。'
    },

    // ============================================
    // Discount Application Names
    // ============================================
    DISCOUNT_NAMES: {
      'kwh_macau': '澳門鏡湖醫院員工優惠',
      'space_vip': 'Space VIP優惠',
      'skhwc_staff': '聖公會福利協會員工優惠',
      'ibd_group': '結腸友會會員優惠',
      'fubon': '富邦銀行職員優惠',
      'citibank': '花旗銀行職員優惠',
      'cumc': '中文大學醫院職員優惠',
      'liuyang': '劉洋歌迷會優惠',
      'partner_staff': '伙伴企業員工折扣',
      'staff': '💼 員工優惠',
      'dch_auriga': 'DCH_Auriga 職員優惠'
    }
  };

  /**
   * DOM Element IDs for quick reference
   * @namespace GniibPromotions.DomIds
   */
  global.GniibPromotions.DomIds = {
    LOADING: 'promotion-loading',
    ERROR: 'promotion-error',
    ERROR_MESSAGE: 'error-message',
    CONTENT: 'promotion-content',
    CAMPAIGNS: 'campaigns-container',
    PRODUCTS_GRID: 'products-grid',
    SELECTED_ITEMS: 'selected-items',
    MONTHLY_USED: 'monthly-used',
    REMAINING_ALLOWANCE: 'remaining-allowance',
    SUBTOTAL: 'subtotal-amount',
    CHECKOUT_BTN: 'checkout-button',
    RETRY_BTN: 'retry-button',
    LOGIN_BTN: 'login-button',
    REGISTER_BTN: 'register-button',
    // Mobile elements
    MOBILE_SUMMARY: 'mobile-summary',
    MOBILE_ITEM_COUNT: 'mobile-item-count',
    MOBILE_SUBTOTAL: 'mobile-subtotal',
    MOBILE_SELECTED_ITEMS: 'mobile-selected-items',
    MOBILE_MONTHLY_USED: 'mobile-monthly-used',
    MOBILE_REMAINING_ALLOWANCE: 'mobile-remaining-allowance',
    MOBILE_SUBTOTAL_AMOUNT: 'mobile-subtotal-amount',
    MOBILE_CHECKOUT_BTN: 'mobile-checkout-button',
    SUMMARY_COMPACT_TOGGLE: 'summary-compact-toggle',
    SUMMARY_CLOSE_BTN: 'summary-close-btn',
    SUMMARY_BACKDROP: 'summary-backdrop'
  };

})(window);
